import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Tabs,
  Switch,
  Select,
  DatePicker,
  GetProps,
} from "antd";
import { Column } from "@ant-design/plots";
import { mixPostV1 } from "../../services/mixin";
import {
  getReportColumns,
  detailColumns,
  matchColumns,
  payoutVoucherColumns,
  bonusColumns,
  manualPayoutColumns,
} from "./component/TablesColumns";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Calendar2 } from "iconsax-react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { RangePickerProps } from "antd/es/date-picker";
import { LineOutlined } from "@ant-design/icons";
import { set } from "@antv/util";

const { RangePicker } = DatePicker;
const disabledDate: RangePickerProps["disabledDate"] = (current) => {
  // Can not select days before today and today
  return current && current > dayjs().endOf("day");
};
const timeOptions = [
  { label: "All", value: "all", type: 0 },
  { label: "Today", value: "today", type: 1 },
  { label: "This week", value: "this_week", type: 2 },
  { label: "This month", value: "this_month", type: 3 },
  { label: "Last week", value: "last_week", type: 4 },
  { label: "Last month", value: "last_month", type: 5 },
];

type StatNumberProps = {
  label: string;
  value: number;
  change: number;
};

const StatNumber = ({ label, value = 0, change }: StatNumberProps) => (
  <div style={{ marginBottom: 2 }}>
    <b>{label}</b>: ${value?.toLocaleString?.() ?? 0}
    {/* <span style={{ color: change > 0 ? "green" : "red", marginLeft: 4 }}>
            ({change > 0 ? "↑" : "↓"} {Math.abs(change)}%)
        </span> */}
  </div>
);

const StaffStatisticsPopup = ({
  isShow,
  onClose,
  staffId,
}: {
  isShow: boolean;
  onClose: () => void;
  staffId?: number;
}) => {
  const [chartType, setChartType] = useState<string>("Profit");
  const [timeRange, setTimeRange] = useState<string>("all");
  const [statData, setStatData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isAverages, setIsAverages] = useState(false); // Thêm state này

  // Thêm state cho chartData và tableData nếu cần
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [isShowDetail, setIsShowDetail] = useState(false);
  const [detailData, setDetailData] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [matchData, setMatchData] = useState<any[]>([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchPage, setMatchPage] = useState(1);
  const [matchTotal, setMatchTotal] = useState(0);
  const [reportPage, setReportPage] = useState(1);
  const [reportTotal, setReportTotal] = useState(0);
  const [payoutVoucherData, setPayoutVoucherData] = useState<any[]>([]);
  const [payoutVoucherLoading, setPayoutVoucherLoading] = useState(false);
  const [payoutVoucherPage, setPayoutVoucherPage] = useState(1);
  const [payoutVoucherTotal, setPayoutVoucherTotal] = useState(0);
  const [payoutNormalData, setPayoutNormalData] = useState<any[]>([]);
  const [payoutNormalLoading, setPayoutNormalLoading] = useState(false);
  const [payoutNormalPage, setPayoutNormalPage] = useState(1);
  const [payoutNormalTotal, setPayoutNormalTotal] = useState(0);
  const [bonusData, setBonusData] = useState<any[]>([]);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [bonusPage, setBonusPage] = useState(1);
  const [bonusTotal, setBonusTotal] = useState(0);
  const [manualPayoutData, setManualPayoutData] = useState<any[]>([]);
  const [manualPayoutLoading, setManualPayoutLoading] = useState(false);
  const [manualPayoutPage, setManualPayoutPage] = useState(1);
  const [manualPayoutTotal, setManualPayoutTotal] = useState(0);
  const [fromDate, setFromDate] = useState<string>();
  const [toDate, setToDate] = useState<string>();
  const [isPicker, setIsPicker] = useState<boolean>(false);
  const [rangeValue, setRangeValue] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const onRangeChange = (
    dates: null | (Dayjs | null)[],
    dateStrings: string[]
  ) => {
    setRangeValue(dates as [Dayjs | null, Dayjs | null] | null);
    if (dates) {
      setIsPicker(true);
      setFromDate(dateStrings[0]);
      setToDate(dateStrings[1]);
    } else {
      setIsPicker(false);
      setFromDate("");
      setToDate("");
    }
  };
  // Lấy dữ liệu thống kê khi mở popup hoặc đổi staffId/timeRange
  const getDataStaff = async () => {
    if (!isShow || !staffId) return;
    setLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1("/api/webAdmin/gReportStatisticStaff.php", {
      staff_id: staffId,
      type_report,
      from_date: fromDate,
      to_date: toDate,
    });
    if (res.status === 1 && res.data) {
      setStatData(res.data);
    } else {
      setStatData(null);
    }
    setLoading(false);
  };
  const getChartData = async () => {
    if (!isShow || !staffId) return;
    setLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    // chartType: "Profit", "Match", "Payout", "MachineProfit", "Extra"
    // API yêu cầu id_name_report: 1: profit, 2: match, 3: payout, 4: profit machine, 5: extra
    const chartTypeMap: Record<string, number> = {
      Profit: 1,
      Match: 2,
      Payout: 3,
      MachineProfit: 4,
      Extra: 5,
    };
    const id_name_report = chartTypeMap[chartType] || 1;
    const res = await mixPostV1(
      "/api/webAdmin/gReportChartStatisticStaff.php",
      {
        staff_id: staffId,
        type_report,
        id_name_report,
        from_date: fromDate,
        to_date: toDate,
      }
    );
    if (res.status === 1 && Array.isArray(res.data)) {
      setChartData(res.data);
    } else {
      setChartData([]);
    }
    setLoading(false);
  };

  // Hàm lấy dữ liệu bảng Report (Total Income By Staff)
  const getReportTableData = async (page = 1) => {
    if (!isShow || !staffId) return;
    setLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1("/api/webAdmin/greportTotalIncomeByStaff.php", {
      staff_id: staffId,
      type_report,
      limit: 5, // pageSize của Table
      page_number: page,
      from_date: fromDate,
      to_date: toDate,
    });
    if (res.status === 1 && Array.isArray(res.data)) {
      const mapped = res.data.map((item: any, idx: number) => ({
        ...item,
        key: item.report_id || idx,
        date: item.date_report,
        staff: item.staff_name,
      }));
      setTableData(mapped);
      setReportTotal(res.totalRecord || 0);
    } else {
      setTableData([]);
      setReportTotal(0);
    }
    setLoading(false);
  };
  //hàm lấy dữ liệu detail income theo nhân viên
  const fetchDetailIncome = async (report_id: number) => {
    setDetailLoading(true);
    const res = await mixPostV1("/api/webAdmin/gDetailIncomeByStaff.php", {
      report_id,
    });
    if (res.status === 1 && Array.isArray(res.data)) {
      setDetailData(
        res.data.map((item: any, idx: number) => ({
          ...item,
          key: item.info_id || idx,
          date: item.date_report,
          staff: item.staff_name,
        }))
      );
    } else {
      setDetailData([]);
    }
    setDetailLoading(false);
    setIsShowDetail(true);
  };

  // Hàm lấy dữ liệu bảng Match
  const getMatchTableData = async (page = 1) => {
    if (!isShow || !staffId) return;
    setMatchLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1("/api/webAdmin/gListMatchByStaff.php", {
      staff_id: staffId,
      type_report,
      from_date: fromDate,
      to_date: toDate,
      limit: 5, // pageSize của Table
      page_number: page,
    });
    if (res.status === 1 && Array.isArray(res.data)) {
      setMatchData(
        res.data.map((item: any, idx: number) => ({
          ...item,
          key: item.app_id || idx,
        }))
      );
      setMatchTotal(res.total || 0);
    } else {
      setMatchData([]);
      setMatchTotal(0);
    }
    setMatchLoading(false);
  };

  // Hàm lấy dữ liệu bảng Payout Voucher
  const getPayoutVoucherData = async (page = 1) => {
    if (!isShow || !staffId) return;
    setPayoutVoucherLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1(
      "/api/webAdmin/gReportPayoutVoucherByStaff.php",
      {
        staff_id: staffId,
        type_report,
        limit: 5,
        page_number: page,
        from_date: fromDate,
        to_date: toDate,
      }
    );
    if (res.status === 1 && Array.isArray(res.data)) {
      setPayoutVoucherData(
        res.data.map((item: any, idx: number) => ({
          ...item,
          key: item.id || idx,
        }))
      );
      setPayoutVoucherTotal(res.total || 0);
    } else {
      setPayoutVoucherData([]);
      setPayoutVoucherTotal(0);
    }
    setPayoutVoucherLoading(false);
  };

  // Hàm lấy dữ liệu bảng Payout Normal
  const getPayoutNormalData = async (page = 1) => {
    if (!isShow || !staffId) return;
    setPayoutNormalLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1(
      "/api/webAdmin/gReportPayoutNormalByStaff.php",
      {
        staff_id: staffId,
        type_report,
        limit: 5,
        page_number: page,
        from_date: fromDate,
        to_date: toDate,
      }
    );
    if (res.status === 1 && Array.isArray(res.data)) {
      setPayoutNormalData(
        res.data.map((item: any, idx: number) => ({
          ...item,
          key: item.id || idx,
        }))
      );
      setPayoutNormalTotal(res.total || 0);
    } else {
      setPayoutNormalData([]);
      setPayoutNormalTotal(0);
    }
    setPayoutNormalLoading(false);
  };

  // Hàm lấy dữ liệu bảng Bonus
  const getBonusData = async (page = 1) => {
    if (!isShow || !staffId) return;
    setBonusLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1("/api/webAdmin/gReportPayBonusByStaff.php", {
      staff_id: staffId,
      type_report,
      limit: 5,
      page_number: page,
      from_date: fromDate,
      to_date: toDate,
    });
    if (res.status === 1 && Array.isArray(res.data)) {
      setBonusData(
        res.data.map((item: any, idx: number) => ({
          ...item,
          key: item.pay_bonus_id || idx,
          time: item.timecashout || item.create_at,
          cashOut: item.cash_out_amount || item.match_amount,
          machine: item.machine_number,
          photo: item.photo_url,
        }))
      );
      setBonusTotal(res.total || 0);
    } else {
      setBonusData([]);
      setBonusTotal(0);
    }
    setBonusLoading(false);
  };

  // Hàm lấy dữ liệu bảng Manual Payout
  const getManualPayoutData = async (page = 1) => {
    if (!isShow || !staffId) return;
    setManualPayoutLoading(true);
    const type_report = isPicker
      ? 6
      : timeOptions.find((opt) => opt.value === timeRange)?.type ?? 0;
    const res = await mixPostV1(
      "/api/webAdmin/gReportManualPayoutBySatff.php",
      {
        staff_id: staffId,
        type_report,
        limit: 5,
        page_number: page,
        from_date: fromDate,
        to_date: toDate,
      }
    );
    if (res.status === 1 && Array.isArray(res.data)) {
      setManualPayoutData(
        res.data.map((item: any, idx: number) => ({
          ...item,
          key: item.id || idx,
          time: item.timecashout || item.create_at,
          cashOut: item.match_amount,
          actualCashOut: item.amount_machine,
          machine: item.machine_number,
        }))
      );
      setManualPayoutTotal(res.total || 0);
    } else {
      setManualPayoutData([]);
      setManualPayoutTotal(0);
    }
    setManualPayoutLoading(false);
  };

  // Hàm lấy giá trị trung bình nếu bật Averages
  const getValue = (field: string) => {
    if (!statData) return 0;
    const value = Number(statData[field]) || 0;
    if (isAverages && Number(statData.total_shift) > 0) {
      return Math.round(value / Number(statData.total_shift));
    }
    return value;
  };

  // Chart config: lấy từ statData nếu có, nếu không thì để mảng rỗng
  const chartSource = chartData.length > 0 ? chartData : [];
  const columnConfig = {
    data: chartSource,
    xField: "date",
    yField: "value",
    seriesField: "name",
    columnWidthRatio: 0.2,
    minColumnWidth: 6,
    maxColumnWidth: 12,
    color: "#4096ff",
    columnStyle: { radius: [6, 6, 0, 0] },
    // style: {
    //   radiusTopLeft: 6,
    //   radiusTopRight: 6,
    // },
    label: {
      position: "top" as const,
      style: { fontSize: 11, fill: "#333", fontWeight: 500 },
      formatter: (datum: any) => {
        const value = Number(datum);
        return !isNaN(value) && value !== 0 ? value.toLocaleString() : "";
      },
    },
    animation: {
      appear: { animation: "scale-in-y", duration: 800 },
    },
    height: 240,
    padding: "auto",
    xAxis: {
      label: {
        autoRotate: false,
        autoHide: false,
        autoEllipsis: false,
        rotate: Math.PI / 3, // 60°
        offset: 12, // đẩy nhãn ra khỏi trục một chút
        style: { fontSize: 10, fill: "#666" },
      },
    },
    appendPadding: [36, 36, 56, 48],
  };

  // Tab items: dùng tableData nếu có
  const tabItems = [
    {
      key: "report",
      label: "Report",
      children: (
        <Table
          columns={getReportColumns((record) =>
            fetchDetailIncome(record.report_id)
          )}
          dataSource={tableData}
          size="small"
          pagination={{
            current: reportPage,
            pageSize: 5,
            total: reportTotal,
            onChange: (page) => setReportPage(page),
            showSizeChanger: false,
          }}
          loading={loading}
          scroll={{ x: true }}
        />
      ),
    },
    {
      key: "match",
      label: "Match",
      children: (
        <Table
          columns={matchColumns}
          dataSource={matchData}
          size="small"
          pagination={{
            current: matchPage,
            pageSize: 5,
            total: matchTotal,
            onChange: (page) => setMatchPage(page),
            showSizeChanger: false,
          }}
          loading={matchLoading}
          scroll={{ x: true }}
        />
      ),
    },
    {
      key: "payoutvoucher",
      label: "Payout Voucher",
      children: (
        <Table
          columns={payoutVoucherColumns}
          dataSource={payoutVoucherData}
          size="small"
          pagination={{
            current: payoutVoucherPage,
            pageSize: 5,
            total: payoutVoucherTotal,
            onChange: (page) => setPayoutVoucherPage(page),
            showSizeChanger: false,
          }}
          loading={payoutVoucherLoading}
          scroll={{ x: true }}
        />
      ),
    },
    {
      key: "payoutnomal",
      label: "Payout Normal",
      children: (
        <Table
          columns={payoutVoucherColumns}
          dataSource={payoutNormalData}
          size="small"
          pagination={{
            current: payoutNormalPage,
            pageSize: 5,
            total: payoutNormalTotal,
            onChange: (page) => setPayoutNormalPage(page),
            showSizeChanger: false,
          }}
          loading={payoutNormalLoading}
          scroll={{ x: true }}
        />
      ),
    },
    {
      key: "bonus",
      label: "Bonus",
      children: (
        <Table
          columns={bonusColumns}
          dataSource={bonusData}
          size="small"
          pagination={{
            current: bonusPage,
            pageSize: 5,
            total: bonusTotal,
            onChange: (page) => setBonusPage(page),
            showSizeChanger: false,
          }}
          loading={bonusLoading}
          scroll={{ x: true }}
        />
      ),
    },
    {
      key: "manualpayout",
      label: "Manual Payout",
      children: (
        <Table
          columns={manualPayoutColumns}
          dataSource={manualPayoutData}
          size="small"
          pagination={{
            current: manualPayoutPage,
            pageSize: 5,
            total: manualPayoutTotal,
            onChange: (page) => setManualPayoutPage(page),
            showSizeChanger: false,
          }}
          loading={manualPayoutLoading}
          scroll={{ x: true }}
        />
      ),
    },
  ];

  useEffect(() => {
    getDataStaff();
  }, [isShow, staffId, timeRange, rangeValue]);

  // Gọi hàm này khi staffId, timeRange, chartType thay đổi
  useEffect(() => {
    getChartData();
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, chartType, rangeValue]);

  // Gọi hàm này khi mở popup hoặc đổi staffId/timeRange
  useEffect(() => {
    getReportTableData(reportPage);
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, rangeValue, reportPage]);
  useEffect(() => {
    getMatchTableData(matchPage);
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, rangeValue, matchPage]);
  useEffect(() => {
    getPayoutVoucherData(payoutVoucherPage);
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, rangeValue, payoutVoucherPage]);
  useEffect(() => {
    getPayoutNormalData(payoutNormalPage);
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, rangeValue, payoutNormalPage]);
  useEffect(() => {
    getBonusData(bonusPage);
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, rangeValue, bonusPage]);
  useEffect(() => {
    getManualPayoutData(manualPayoutPage);
    // eslint-disable-next-line
  }, [isShow, staffId, timeRange, rangeValue, manualPayoutPage]);

  useEffect(() => {
    setMatchPage(1);
  }, [isShow, staffId, timeRange, rangeValue]);

  useEffect(() => {
    setReportPage(1);
  }, [isShow, staffId, timeRange, rangeValue]);

  useEffect(() => {
    setPayoutVoucherPage(1);
  }, [isShow, staffId, timeRange, rangeValue]);

  useEffect(() => {
    setPayoutNormalPage(1);
  }, [isShow, staffId, timeRange, rangeValue]);

  useEffect(() => {
    setBonusPage(1);
  }, [isShow, staffId, timeRange, rangeValue]);

  useEffect(() => {
    setManualPayoutPage(1);
  }, [isShow, staffId, timeRange, rangeValue]);

  const series = [
    {
      name: chartType,
      data: chartData.map((item) => item.value),
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "30px", // chỉnh độ rộng cột (ví dụ: "40%")

        dataLabels: {
          position: "top", // top, center, bottom
        },
        colors: {
          ranges: [
            {
              from: -9999999999999,
              to: -1,
              color: "#f4664a", // 🔶 Cam đậm cho giá trị âm
            },
            {
              from: 0,
              to: 99999999999,
              color: "#4096ff", // 🔵 Xanh cho giá trị dương
            },
          ],
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return "";
      },
      offsetY: -20,
      style: {
        fontSize: "12px",
        colors: ["#304758"],
      },
    },
    xaxis: {
      categories: chartData.map((item) => item.date),
      position: "bottom",
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: "gradient",
          gradient: {
            colorFrom: "#D8E3F0",
            colorTo: "#BED1E6",
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: {
        enabled: true,
      },
    },
    title: {
      text:
        chartType +
        " In " +
        (timeOptions.find((opt) => opt.value === timeRange)?.label ?? ""),
      floating: true,
      offsetY: 330,
      align: "center",
      style: {
        color: "#444",
      },
    },
  };
  return (
    <>
      <Modal
        open={isShow}
        onCancel={onClose}
        footer={null}
        width={1200}
        title={
          <span style={{ fontWeight: 600, fontSize: 22 }}>
            Staff Statistics
          </span>
        }
        centered
        style={{ top: 24 }}
        bodyStyle={{ background: "#fff", padding: 0 }}
      >
        <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
          {/* Top bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            {/* Bên trái: Select + DatePicker */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Select
                value={timeRange}
                options={timeOptions}
                style={{
                  minWidth: 120,
                  border: `1px solid ${!isPicker ? "#4096ff" : "#d9d9d9"}`,
                  borderRadius: 6,
                  height: 32,
                  background: "#fff",
                  color: "#222",
                }}
                onChange={(val) => {
                  setTimeRange(val);
                  setIsPicker(false);
                  setRangeValue(null);
                }}
              />
              <RangePicker
                allowClear
                value={rangeValue}
                className="rs-range-picker"
                suffixIcon={<Calendar2 size={20} />}
                separator={<LineOutlined />}
                placeholder={["From date", "To date"]}
                style={{
                  background: "#fff",
                  color: "#222",
                  border: `1px solid ${isPicker ? "#4096ff" : "#d9d9d9"} `,
                  borderRadius: 6,
                  minWidth: 120,
                  height: 32,
                }}
                disabledDate={disabledDate}
                onChange={onRangeChange}
              />
            </div>
            {/* Bên phải: Switch Averages */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 500 }}>Averages</span>
              <Switch
                size="small"
                checked={isAverages}
                onChange={setIsAverages}
              />
            </div>
          </div>

          {/* Info */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div style={{ minWidth: 320 }}>
              <div style={{ fontSize: 18, marginBottom: 2 }}>
                <b>Name</b>:{" "}
                <span style={{ fontWeight: 600 }}>
                  {statData?.staff_name || ""}
                </span>
              </div>
              <div style={{ fontSize: 16, marginBottom: 2 }}>
                <b>Shifts Worked</b>: {statData?.total_shift || 0}
              </div>
              <StatNumber
                label="Total Match"
                value={getValue("total_match")}
                change={0}
              />
              <StatNumber
                label="Total Extra"
                value={getValue("total_extra")}
                change={0}
              />
              <StatNumber
                label="Machine profit"
                value={getValue("machine_profit")}
                change={0}
              />
            </div>
            <div style={{ minWidth: 320 }}>
              <div style={{ fontSize: 18, marginBottom: 2 }}>
                <b>Phone number</b>:{" "}
                <span style={{ fontWeight: 600 }}>
                  {statData?.phone_number || ""}
                </span>
              </div>
              <StatNumber
                label="Total Profit"
                value={getValue("total_profit")}
                change={0}
              />
              <StatNumber
                label="Total Payout"
                value={getValue("total_payout")}
                change={0}
              />
              <StatNumber
                label="Bonus"
                value={getValue("total_bonus")}
                change={0}
              />
              <StatNumber
                label="Total loss"
                value={getValue("total_loss")}
                change={0}
              />
            </div>
          </div>

          {/* Chart + Buttons */}
          <div style={{ display: "flex", gap: 32 }}>
            <div
              style={{
                flex: 1,
                border: "1px solid #eee",
                borderRadius: 8,
                background: "#fafbfc",
                minHeight: 220,
                boxShadow: "0 2px 8px #0001",
              }}
            >
              {/* <Column {...columnConfig} /> */}
              <ApexCharts
                options={options}
                series={series}
                type="bar"
                height={350}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Button
                type={chartType === "Profit" ? "primary" : "default"}
                onClick={() => setChartType("Profit")}
              >
                Profit
              </Button>
              <Button
                type={chartType === "Match" ? "primary" : "default"}
                onClick={() => setChartType("Match")}
              >
                Match
              </Button>
              <Button
                type={chartType === "Payout" ? "primary" : "default"}
                onClick={() => setChartType("Payout")}
              >
                Payout
              </Button>
              <Button
                type={chartType === "MachineProfit" ? "primary" : "default"}
                onClick={() => setChartType("MachineProfit")}
              >
                Profit machine
              </Button>
              <Button
                type={chartType === "Extra" ? "primary" : "default"}
                onClick={() => setChartType("Extra")}
              >
                Extra
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ marginTop: 32 }}>
            <Tabs
              defaultActiveKey="report"
              items={tabItems}
              tabBarStyle={{ marginBottom: 8, fontWeight: 600, fontSize: 16 }}
            />
          </div>
        </div>
      </Modal>

      {/* Modal chi tiết thu nhập theo nhân viên */}
      <Modal
        open={isShowDetail}
        onCancel={() => setIsShowDetail(false)}
        footer={null}
        width={900}
        title="Detail Income By Staff"
      >
        <Table
          columns={detailColumns}
          dataSource={detailData}
          loading={detailLoading}
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
      </Modal>
    </>
  );
};

export default StaffStatisticsPopup;
